import { clsx } from 'clsx'
import { subHours } from 'date-fns'
import { useAnalytics } from '@/api/hooks/use-analytics'
import { useAuth } from '@/lib/auth-context'

// Warning thresholds — matches the abuse system (one-abuse/src/thresholds.rs).
// These are the levels where enforcement starts (throttle to 50%).
const BOUNCE_THRESHOLD = 3
const COMPLAINT_THRESHOLD = 0.05

export function AccountHealthSection(): React.JSX.Element {
  const { account } = useAuth()
  // Use 24h window to match the abuse system's evaluation period
  const { data: analyticsData, isLoading } = useAnalytics({
    from: subHours(new Date(), 24),
    to: new Date(),
    granularity: 'hour',
  })

  if (isLoading) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Account Health</h2>
        <p className="mt-4 text-center text-sm text-text-tertiary">Loading health metrics...</p>
      </div>
    )
  }

  if (!analyticsData) {
    return (
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Account Health</h2>
        <p className="mt-4 text-center text-sm text-text-tertiary">
          No data yet — send your first email to see health metrics.
        </p>
      </div>
    )
  }

  // Analytics returns rates as ratios (0-1), convert to percentages
  const bouncePercent = analyticsData.totals.bounce_rate * 100
  const complaintPercent = analyticsData.totals.complaint_rate * 100

  const level = account?.suspension_level ?? 0
  const bounceStatus = rateStatus(bouncePercent, BOUNCE_THRESHOLD)
  const complaintStatus = rateStatus(complaintPercent, COMPLAINT_THRESHOLD)
  const { healthLabel, healthStatus } = deriveHealth(level, bouncePercent, complaintPercent)

  // Show recovery note when throttled but rates have improved
  const isThrottled = level >= 1
  const ratesHealthy = bouncePercent < BOUNCE_THRESHOLD && complaintPercent < COMPLAINT_THRESHOLD

  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">Account Health</h2>
        <span className="rounded-full bg-surface-2 px-2.5 py-0.5 text-xs font-medium text-text-secondary">
          Last 24 hours
        </span>
      </div>
      <p className="mt-1 text-xs text-text-tertiary">
        Rolling 24-hour rates — these are what our abuse system evaluates
      </p>
      <div className="mt-4 space-y-4">
        <HealthMetricRow
          label="Bounce Rate"
          value={`${bouncePercent.toFixed(2)}%`}
          threshold={`< ${BOUNCE_THRESHOLD}%`}
          status={bounceStatus}
        />
        <HealthMetricRow
          label="Complaint Rate"
          value={`${complaintPercent.toFixed(3)}%`}
          threshold={`< ${COMPLAINT_THRESHOLD}%`}
          status={complaintStatus}
        />
        <HealthMetricRow
          label="Health Score"
          value={healthLabel}
          threshold="Healthy"
          status={healthStatus}
        />
      </div>
      {isThrottled && ratesHealthy && (
        <div className="mt-4 rounded-lg border border-success/30 bg-success/10 px-4 py-3">
          <p className="text-sm text-success-700">
            Your rates have improved and are now below threshold. Sending will resume automatically within 24 hours.
          </p>
        </div>
      )}
    </div>
  )
}

function rateStatus(rate: number, threshold: number): 'good' | 'warning' | 'danger' {
  if (rate < threshold * 0.5) return 'good'
  if (rate < threshold) return 'warning'
  return 'danger'
}

function deriveHealth(
  suspensionLevel: number,
  bouncePercent: number,
  complaintPercent: number,
): { healthLabel: string; healthStatus: 'good' | 'warning' | 'danger' } {
  // Account-level enforcement takes priority
  if (suspensionLevel >= 3) return { healthLabel: 'Suspended', healthStatus: 'danger' }
  if (suspensionLevel === 2) return { healthLabel: 'Paused', healthStatus: 'danger' }
  if (suspensionLevel === 1) return { healthLabel: 'Throttled', healthStatus: 'warning' }

  // Fall back to rate-based assessment
  if (bouncePercent > 5 || complaintPercent > 0.3) return { healthLabel: 'At Risk', healthStatus: 'danger' }
  if (bouncePercent < 3 && complaintPercent < 0.1) return { healthLabel: 'Healthy', healthStatus: 'good' }
  return { healthLabel: 'Needs Attention', healthStatus: 'warning' }
}

function HealthMetricRow({
  label,
  value,
  threshold,
  status,
}: {
  label: string
  value: string
  threshold: string
  status: 'good' | 'warning' | 'danger'
}): React.JSX.Element {
  return (
    <div className="flex items-center justify-between rounded-lg bg-surface-1 px-4 py-3">
      <div>
        <p className="text-sm font-medium text-text-primary">{label}</p>
        <p className="text-xs text-text-tertiary">Threshold: {threshold}</p>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-sm font-semibold text-text-primary">{value}</span>
        <span
          className={clsx(
            'inline-block h-3 w-3 rounded-full',
            status === 'good' && 'bg-success',
            status === 'warning' && 'bg-warning',
            status === 'danger' && 'bg-danger',
          )}
        />
      </div>
    </div>
  )
}
